import React, { useState, useEffect } from 'react';
import { Bell, Save, Loader2, AlertCircle, Check, Mail, Smartphone, Megaphone, Heart, MessageSquare, Eye } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminNotificationSettings = () => {
  const [settings, setSettings] = useState({
    new_interest_email: true,
    new_interest_push: true,
    new_message_email: true,
    new_message_push: true,
    profile_view_email: false,
    profile_view_push: true,
    match_email: true,
    match_push: true,
    promotional_email: true,
    promotional_push: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchSettings = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_URL}/api/admin/phase2/notification-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    setSaving(true);
    setError(null);

    try {
      await axios.put(`${API_URL}/api/admin/phase2/notification-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const notificationTypes = [
    {
      key: 'new_interest',
      icon: Heart,
      title: 'New Interest',
      description: 'When someone sends an interest request',
      color: 'rose'
    },
    {
      key: 'new_message',
      icon: MessageSquare,
      title: 'New Message',
      description: 'When a user receives a new message',
      color: 'blue'
    },
    {
      key: 'profile_view',
      icon: Eye,
      title: 'Profile View',
      description: 'When someone views their profile',
      color: 'purple'
    },
    {
      key: 'match',
      icon: Heart,
      title: 'Mutual Match',
      description: 'When two users mutually accept each other',
      color: 'mint'
    },
    {
      key: 'promotional',
      icon: Megaphone,
      title: 'Promotional',
      description: 'Marketing and promotional notifications',
      color: 'amber'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-mint-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-notification-settings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notification Settings</h1>
          <p className="text-gray-600 mt-1">Configure default notification preferences for all users</p>
        </div>
        <Bell className="w-10 h-10 text-mint-500" />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <Check className="w-5 h-5 text-green-500" />
          <p className="text-green-700">{success}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-1">
              <span className="text-sm font-medium text-gray-700">Notification Type</span>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 text-sm font-medium text-gray-700">
                <Mail className="w-4 h-4" />
                Email
              </div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 text-sm font-medium text-gray-700">
                <Smartphone className="w-4 h-4" />
                Push
              </div>
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {notificationTypes.map((type) => (
            <div 
              key={type.key} 
              className="px-6 py-4 hover:bg-gray-50 transition-colors"
              data-testid={`notification-row-${type.key}`}
            >
              <div className="grid grid-cols-3 gap-4 items-center">
                <div className="col-span-1 flex items-center gap-3">
                  <div className={`p-2 bg-${type.color}-50 rounded-lg`}>
                    <type.icon className={`w-4 h-4 text-${type.color}-500`} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{type.title}</p>
                    <p className="text-sm text-gray-500">{type.description}</p>
                  </div>
                </div>
                
                <div className="text-center">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings[`${type.key}_email`]}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        [`${type.key}_email`]: e.target.checked
                      }))}
                      className="sr-only peer"
                      data-testid={`toggle-${type.key}-email`}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-mint-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-mint-500"></div>
                  </label>
                </div>

                <div className="text-center">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings[`${type.key}_push`]}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        [`${type.key}_push`]: e.target.checked
                      }))}
                      className="sr-only peer"
                      data-testid={`toggle-${type.key}-push`}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-mint-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-mint-500"></div>
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between pt-4">
        <div className="text-sm text-gray-500">
          These are default settings. Users can customize their own preferences.
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 bg-mint-500 text-white rounded-lg hover:bg-mint-600 disabled:opacity-50 transition-colors"
          data-testid="save-notification-settings-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save Settings
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">About Notification Settings</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Email notifications are sent to the user's registered email address</li>
          <li>• Push notifications require users to enable browser notifications</li>
          <li>• Users can override these defaults in their account settings</li>
          <li>• Promotional emails follow CAN-SPAM compliance requirements</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminNotificationSettings;
